import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { DownloadIcon, MoreVerticalIcon } from "../../styles/icons";
import { useApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";
import DateUtil from "../../util/DateUtil";
import { emptyStrToNull } from "../../util/StringUtil";

interface EligibleMembersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  memberFilterQuery?: string | null;
  totalMembers: string;
}

export const EligibleMembersDrawer = ({
  isOpen,
  onClose,
  businessId,
  memberFilterQuery,
  totalMembers,
}: EligibleMembersDrawerProps) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const apiClient = useApiClient();
  const { showInfoToast, showSuccessToast, showErrorToast } = useToast();

  // Trigger a refresh when the drawer opens
  useEffect(() => {
    if (isOpen) {
      setRefreshTrigger((prev) => prev + 1);
    }
  }, [isOpen]);

  const downloadCsv = async () => {
    showInfoToast("Preparing download. This may take a moment...");
    try {
      const result =
        await apiClient.downloadNotificationScheduleEligibleMembersAsCsv(
          businessId,
          memberFilterQuery
        );

      if (result.ok) {
        const blobData = new Blob([result.data]);
        const url = URL.createObjectURL(blobData);
        const link = document.createElement("a");
        const now = new Date();
        const pad = (value: number) => value.toString().padStart(2, "0");
        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
          now.getDate()
        )}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(
          now.getSeconds()
        )}`;
        const fileName = `cherrypass-eligible-members-${businessId}-${timestamp}.csv`;
        link.download = fileName;
        link.href = url;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSuccessToast(
          "The eligible members data has been downloaded successfully"
        );
      } else {
        showErrorToast("Failed to download the eligible members data");
      }
    } catch (error) {
      showErrorToast(
        "An error occurred while downloading eligible members data"
      );
      console.error("Download error:", error);
    }
  };

  const {
    items: eligibleMembers,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getNotificationScheduleEligibleMembers(
        businessId,
        memberFilterQuery,
        30
      ),
    // Request subsequent pages
    (apiClient, continuationToken) =>
      apiClient.getNotificationScheduleEligibleMembers(
        businessId,
        memberFilterQuery,
        30,
        continuationToken
      ),
    [businessId, memberFilterQuery, refreshTrigger]
  );

  const drawerContentStyles = {
    maxWidth: "1000px",
    width: "100%",
  };

  if (!isOpen) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      preserveScrollBarGap
    >
      <DrawerOverlay />
      <DrawerContent sx={drawerContentStyles}>
        <Flex position="absolute" right="40px" top="8px" zIndex="1">
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Download options"
              icon={<MoreVerticalIcon />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem onClick={() => downloadCsv()} icon={<DownloadIcon />}>
                Export All Eligible Members (CSV)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <DrawerCloseButton />
        <DrawerHeader>{`ELIGIBLE MEMBERS`}</DrawerHeader>
        <DrawerBody>
          <Box marginBottom={2}>{`Total: ${totalMembers} Members`}</Box>

          {isLoading && (
            <Center>
              <Spinner margin="4" marginTop={"16"} />
            </Center>
          )}

          {error && (
            <ErrorMessage>
              An error occurred while fetching eligible members data.
            </ErrorMessage>
          )}

          {eligibleMembers && eligibleMembers.length > 0 && (
            <>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Membership Number</Th>
                    <Th>Member Name</Th>
                    <Th>Member Level</Th>
                    <Th>Financial Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {eligibleMembers.map((member, index) => (
                    <Tr key={index}>
                      <Td>
                        {emptyStrToNull(member.MembershipNumber) ??
                          emptyStrToNull(member.BadgeNumber) ??
                          "-"}
                      </Td>
                      <Td>{`${member.FirstName || ""} ${
                        member.LastName || ""
                      }`}</Td>
                      <Td>{member.MemberLevelDescription || "-"}</Td>
                      <Td>
                        {DateUtil.formatDate(member.DateFinancial, "en-AU", {
                          dateStyle: "medium",
                        }) ?? "-"}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {fetchNextPage && (
                <Box mt={4} display="flex">
                  {!isLoadingPage && (
                    <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                      Load more...
                    </Button>
                  )}
                  {isLoadingPage && <Spinner />}
                </Box>
              )}
            </>
          )}

          {!isLoading && (!eligibleMembers || eligibleMembers.length === 0) && (
            <Box textAlign="center" py={4}>
              No eligible members found.
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
